import { LatLng } from '@fe/core';
import { GEO_COORD_INT } from '../messages/message-parser';
import { StaticAirport } from '../static/static-airports';

export default class Coordinate implements LatLng {
    public readonly isValid: boolean;

    constructor(
        public readonly lat: number,
        public readonly lng: number
    ) {
        this.isValid = lat <= 90 && lat >= -90 && lng <= 180 && lng >= -180;
    }

    static from10k(lat10k: number, lng10k: number): Coordinate {
        if (lat10k === 2000000 || lng10k === 2000000) {
            return Coordinate.empty;
        }
        return new Coordinate((lat10k - 900_000) / 10_000, (lng10k - 1_800_000) / 10_000);
    }

    static fromSigned10k(lat10k: number, lng10k: number): Coordinate {
        return new Coordinate(lat10k / 10_000, lng10k / 10_000);
    }

    static from100k(lat100k: number, lng100k: number): Coordinate {
        return new Coordinate((lat100k - 9_000_000) / 100_000, (lng100k - 18_000_000) / 100_000);
    }

    static fromSigned100k(lat100k: number, lng100k: number): Coordinate {
        return new Coordinate(lat100k / 100_000, lng100k / 100_000);
    }

    static fromGeoCord(cord: GEO_COORD_INT): Coordinate {
        return new Coordinate(
            (cord.cLatUpper4 * 65_536 + cord.wLatitude - 180_000) / 1_000,
            (cord.cLonUpper4 * 65_536 + cord.wLongitude - 180_000) / 1_000
        );
    }

    static fromLatLng(lat: number, lng: number) {
        return new Coordinate(lat, lng);
    }

    static fromArray(lngLat: [number, number] | undefined | null): Coordinate {
        if (!lngLat) {
            return Coordinate.empty;
        }
        return Coordinate.fromLatLng(lngLat[1], lngLat[0]);
    }

    static readonly empty = new Coordinate(200, 200);

    headingTo(other: Coordinate): number | null {
        if (!this.isValid || !other.isValid) {
            return null;
        }

        return Math.round(calculateHeading(this, other));
    }

    distanceTo(lat: number, lng: number): number | null {
        if (!this.isValid) {
            return null;
        }

        return calculateDistance(this.lat, this.lng, lat, lng);
    }

    distanceToAirport(airport: StaticAirport) {
        if (!this.isValid || !airport.lat || !airport.lng) {
            return null;
        }
        return calculateDistance(this.lat, this.lng, airport.lat, airport.lng);
    }

    array(): [number, number] | null {
        return this.isValid ? [this.lng, this.lat] : null;
    }

    toRouteString(): string {
        if (!this.isValid) {
            return '';
        }

        // ex: 3557N/08138W for 35.57, -81.38
        const [latDeg, latMin] = toDegMin(this.lat);
        const latStr = `${String(latDeg).padStart(2, '0')}${String(latMin).padStart(2, '0')}${this.lat >= 0 ? 'N' : 'S'}`;

        const [lngDeg, lngMin] = toDegMin(this.lng);
        const lngStr = `${String(lngDeg).padStart(3, '0')}${String(lngMin).padStart(2, '0')}${this.lng >= 0 ? 'E' : 'W'}`;

        return `${latStr}/${lngStr}`;
    }

    equals(other: Coordinate): boolean {
        if (!this.isValid || !other.isValid) {
            return false;
        }

        return this.lat === other.lat && this.lng === other.lng;
    }
}

function toDegMin(decimal: number): [number, number] {
    const abs = Math.abs(decimal);
    const deg = Math.floor(abs);
    const min = Math.round((abs - deg) * 60);
    return [deg, min];
}
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number) {
    const milesPerDeg = 60;
    const origLatRad = (lat1 * Math.PI) / 180;
    const origLonRad = (lng1 * Math.PI) / 180;
    const destLatRad = (lat2 * Math.PI) / 180;
    const destLonRad = (lng2 * Math.PI) / 180;

    const centAng = Math.acos(
        Math.sin(origLatRad) * Math.sin(destLatRad) +
            Math.cos(origLatRad) * Math.cos(destLatRad) * Math.cos(Math.abs(origLonRad - destLonRad))
    );
    return (milesPerDeg * centAng * 180) / Math.PI;
}

function calculateHeading(origin: Coordinate, destination: Coordinate): number {
    const origLatRad = (origin.lat * Math.PI) / 180;
    const origLonRad = (origin.lng * Math.PI) / 180;
    const destLatRad = (destination.lat * Math.PI) / 180;
    const destLonRad = (destination.lng * Math.PI) / 180;

    const centAng = Math.acos(
        Math.sin(origLatRad) * Math.sin(destLatRad) +
            Math.cos(origLatRad) * Math.cos(destLatRad) * Math.cos(Math.abs(origLonRad - destLonRad))
    );
    const tmp = Math.cos(origLatRad) * Math.sin(centAng);
    if (tmp == 0.0) {
        return 0.0;
    }

    const arg = (Math.sin(destLatRad) - Math.sin(origLatRad) * Math.cos(centAng)) / tmp;
    if (Math.abs(arg) > 1.0) {
        return 0.0;
    }

    const result = (Math.acos(arg) * 180) / Math.PI;

    return Math.sin(origLonRad - destLonRad) >= 0 ? 360 - result : result;
}
